import { useMemo } from "react";
import {
  Body,
  Caption,
  CardTitle,
  Rail,
  Strong,
} from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { parseAgreement } from "./agreementBlocks";

/**
 * The agreement, typeset.
 *
 * It arrived as one pre-wrapped run of 12px text where a heading, a section
 * title and a clause all looked the same, so the only way to find your place
 * in it was to read it from the top. Headings are rails, sections carry their
 * number, and a clause hangs off its letter, which is how the Word file
 * counsel approved reads. Body size, not caption size: this is the one thing
 * on the page someone is asked to read in full.
 */
export function AgreementText({ text }: { text: string }) {
  const blocks = useMemo(() => parseAgreement(text), [text]);
  return (
    <div className="flex flex-col gap-nf-ds-3 text-nf-ds-fg">
      {blocks.map((block, index) => {
        // Air above anything that starts a new part of the document, so a
        // heading belongs to what follows it rather than to what came before.
        const opens = index > 0 && "mt-nf-ds-4";
        const key = `${index}-${block.kind}`;
        switch (block.kind) {
          case "heading":
            return (
              <Rail key={key} as="h3" muted className={cn(opens)}>
                {block.text}
              </Rail>
            );
          case "title":
            return (
              <CardTitle key={key} as="h3">
                {block.text}
              </CardTitle>
            );
          case "section":
            return (
              <h4
                key={key}
                className={cn("flex items-baseline gap-nf-ds-3", opens)}
              >
                <Caption muted>{block.number}</Caption>
                <Strong>{block.text}</Strong>
              </h4>
            );
          case "clause":
            return (
              <p key={key} className="flex items-baseline gap-nf-ds-3">
                <Caption muted className="shrink-0">
                  {block.marker}
                </Caption>
                <Body as="span">{block.text}</Body>
              </p>
            );
          default:
            return <Body key={key}>{block.text}</Body>;
        }
      })}
    </div>
  );
}
